import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface NoteDevoirMatrice {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  devoir1: number | null;
  devoir2: number | null;
  moyenne_devoirs: number | null;
  absent_devoir1: boolean;
  absent_devoir2: boolean;
  verrouille: boolean;
}

export interface MatriceResponse {
  classe: string;
  matiere: string;
  credits: number;
  notes: NoteDevoirMatrice[];
}

export interface NoteDevoir {
  id: number;
  etudiant: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  etudiant_classe?: string;
  matiere: number;
  matiere_code: string;
  matiere_nom: string;
  note1: string | null;
  absent1: boolean;
  note2: string | null;
  absent2: boolean;
  moyenne_devoirs: string | null;
  verrouille: boolean;
  date_limite: string | null;
  est_verrouille: boolean;
  saisi_par: number | null;
  saisi_par_nom: string | null;
  modifie_par: number | null;
  modifie_par_nom: string | null;
  date_saisie: string;
  date_modification: string;
  annee_academique: string;
  semestre: 'S1' | 'S2';
}

export interface NoteDevoirCreate {
  etudiant_id: number;
  matiere_id: number;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  note1?: number | null;
  absent1?: boolean;
  note2?: number | null;
  absent2?: boolean;
}

export interface NoteDevoirLigne {
  matricule: string;
  matiere_id: number;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  note1?: number | null;
  absent1?: boolean;
  note2?: number | null;
  absent2?: boolean;
}

export interface NoteDevoirMatricielle {
  matiere_id: number;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  notes: Array<{
    etudiant_id: number;
    note1?: number | null;
    absent1?: boolean;
    note2?: number | null;
    absent2?: boolean;
  }>;
}

export interface HistoriqueModification {
  id: number;
  note_devoir: number;
  // Étudiant
  etudiant_matricule: string;
  etudiant_nom?: string;
  etudiant_prenoms?: string;
  // Matière / contexte
  matiere_id?: number;
  matiere_nom: string;
  classe?: string;
  semestre?: string;
  annee_academique?: string;
  // Modification
  champ_modifie: string;                   // note1 | note2 | absent1 | absent2
  champ_modifie_display?: string;          // "Note D.1", etc.
  ancienne_valeur: string | null;
  nouvelle_valeur: string | null;
  motif: string;
  motif_display: string;
  commentaire: string;
  // Agent
  modifie_par: number | null;
  modifie_par_nom: string | null;          // legacy (username)
  modifie_par_username?: string;
  modifie_par_nom_complet?: string;
  // Traçabilité
  date_modification: string;
  adresse_ip: string | null;
  batch_id?: string | null;
  type_operation?: string;
}

export interface StatsNotesDevoirs {
  total_notes: number;
  notes_avec_d1: number;
  notes_avec_d2: number;
  notes_completes: number;
  moyenne_d1: number;
  moyenne_d2: number;
  moyenne_generale: number;
  notes_verrouillees: number;
  absents_d1: number;
  absents_d2: number;
  taux_absence_d1: number;
  taux_absence_d2: number;
}

export interface RapportNoteMatiere {
  matiere_code: string;
  matiere_nom: string;
  ue_code: string;
  ue_nom: string;
  credits: number;
  notes: Array<{
    matricule: string;
    nom: string;
    prenoms: string;
    classe: string;
    note1: number | null;
    absent1: boolean;
    note2: number | null;
    absent2: boolean;
    moyenne_devoirs: number | null;
  }>;
  stats: {
    total: number;
    moyenne_classe: number;
    taux_reussite: number;
    absents_d1: number;
    absents_d2: number;
  };
}

export interface RapportImpressionResponse {
  filtres: Record<string, string>;
  matieres: RapportNoteMatiere[];
}

export interface ExcelPreview {
  headers: string[];
  preview_rows: any[][];
  mapping_suggere: { [key: string]: number };
  total_rows: number;
}

export interface ImportResult {
  success: boolean;
  total: number;
  crees: number;
  modifies: number;
  erreurs: number;
  details: Array<{
    etudiant_id?: number;
    status: string;
    moyenne?: string;
    error?: string;
  }>;
  erreurs_parsing?: string[];
}

export const notesDevoirsApi = createApi({
  reducerPath: 'notesDevoirsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['NotesDevoirs', 'HistoriqueModifications', 'Stats'],
  endpoints: (builder) => ({
    // Lister les notes avec filtres
    getNotesDevoirs: builder.query<NoteDevoir[], {
      annee_academique?: string;
      semestre?: string;
      matiere_id?: number;
      etudiant_id?: number;
      verrouille?: boolean;
      classe?: string;
    }>({
      query: (params) => ({
        url: '/notes-devoirs/',
        params,
      }),
      // Transformer la réponse paginée en tableau
      transformResponse: (response: any) => {
        // Si c'est un objet paginé, retourner results
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        // Sinon retourner la réponse directement (déjà un tableau)
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['NotesDevoirs'],
    }),

    // Détail d'une note
    getNoteDevoir: builder.query<NoteDevoir, number>({
      query: (id) => `/notes-devoirs/${id}/`,
      providesTags: ['NotesDevoirs'],
    }),

    // Mode 1: Saisie matricielle
    saisieMatricielle: builder.mutation<ImportResult, NoteDevoirMatricielle>({
      query: (data) => ({
        url: '/notes-devoirs/saisie_matricielle/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // Mode 2: Saisie individuelle
    saisieIndividuelle: builder.mutation<{
      success: boolean;
      created: boolean;
      note_devoir_id: number;
      moyenne_devoirs: string;
    }, NoteDevoirCreate>({
      query: (data) => ({
        url: '/notes-devoirs/saisie_individuelle/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // Mode 3: Saisie ligne par ligne
    saisieLigne: builder.mutation<{
      success: boolean;
      created: boolean;
      note_devoir_id: number;
      moyenne_devoirs: string;
    }, NoteDevoirLigne>({
      query: (data) => ({
        url: '/notes-devoirs/saisie_ligne/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // Prévisualiser Excel
    previewExcel: builder.mutation<ExcelPreview, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/notes-devoirs/preview_excel/',
          method: 'POST',
          body: formData,
        };
      },
    }),

    // Importer Excel
    importExcel: builder.mutation<ImportResult, {
      file: File;
      mapping: { [key: string]: number };
      matiere_id: number;
      annee_academique: string;
      semestre: 'S1' | 'S2';
    }>({
      query: ({ file, mapping, matiere_id, annee_academique, semestre }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mapping', JSON.stringify(mapping));
        formData.append('matiere_id', matiere_id.toString());
        formData.append('annee_academique', annee_academique);
        formData.append('semestre', semestre);
        return {
          url: '/notes-devoirs/import_excel/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // Verrouiller les notes
    verrouiller: builder.mutation<{ success: boolean; nb_notes_verrouillees: number }, {
      matiere_id: number;
      annee_academique: string;
      semestre: 'S1' | 'S2';
      date_limite?: string;
    }>({
      query: (data) => ({
        url: '/notes-devoirs/verrouiller/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesDevoirs'],
    }),

    // Déverrouiller les notes (Super Admin)
    deverrouiller: builder.mutation<{ success: boolean; nb_notes_deverrouillees: number }, {
      matiere_id: number;
      annee_academique: string;
      semestre: 'S1' | 'S2';
    }>({
      query: (data) => ({
        url: '/notes-devoirs/deverrouiller/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesDevoirs'],
    }),

    // Statistiques
    getStats: builder.query<StatsNotesDevoirs, {
      annee_academique?: string;
      semestre?: string;
      matiere_id?: number;
    }>({
      query: (params) => ({
        url: '/notes-devoirs/stats/',
        params,
      }),
      providesTags: ['Stats'],
    }),

    // Historique des modifications (enrichi 2026-08-27 avec filtres contexte)
    getHistorique: builder.query<HistoriqueModification[], {
      note_devoir_id?: number;
      modifie_par?: number;
      date_debut?: string;
      date_fin?: string;
      matiere_id?: number;
      classe?: string;
      semestre?: string;
      annee_academique?: string;
      etudiant_id?: number;
      matricule?: string;
      champ?: string;
      page_size?: number;
    }>({
      query: (params) => ({
        url: '/historique-modifications-notes/',
        params: { ...params, page_size: params.page_size || 500 },
      }),
      // L'endpoint utilise PageNumberPagination — extraire .results
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['HistoriqueModifications'],
    }),

    // Détail historique
    getHistoriqueDetail: builder.query<HistoriqueModification, number>({
      query: (id) => `/historique-modifications-notes/${id}/`,
      providesTags: ['HistoriqueModifications'],
    }),

    // Rapport d'impression groupé par matière
    getRapportImpression: builder.query<RapportImpressionResponse, {
      semestre: string;
      pole?: string;
      niveau?: string;
      parcours?: string;
      option?: string;
      classe?: string;
    }>({
      query: (params) => ({
        url: '/notes-devoirs/rapport_impression/',
        params,
      }),
      providesTags: ['NotesDevoirs'],
    }),

    // Interface matricielle selon cahier des charges
    getMatrice: builder.query<MatriceResponse, {
      classe: string;
      matiere: number;
      semestre: number;
    }>({
      query: (params) => ({
        url: '/notes-devoirs/matrice/',
        params,
      }),
      providesTags: ['NotesDevoirs'],
    }),

    // Mise à jour en masse
    bulkUpdate: builder.mutation<{
      updated: number;
      errors: string[];
    }, {
      notes: Array<{
        id: number;
        devoir1?: number | null;
        devoir2?: number | null;
        absent_devoir1?: boolean;
        absent_devoir2?: boolean;
      }>;
    }>({
      query: (data) => ({
        url: '/notes-devoirs/bulk_update/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // ═══ ACTIONS RAPIDES ═══

    reporterExamenVersDevoirs: builder.mutation<{
      success: boolean; message: string; crees: number; modifies: number; ignores: number; total_traites: number; details: string[];
    }, { matiere_id: number; annee_academique: string; semestre: string; classes?: string[] }>({
      query: (data) => ({ url: '/notes-devoirs/reporter_examen_vers_devoirs/', method: 'POST', body: data }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // Version BATCH : applique le report sur toutes les matières d'un scope
    // (pôle / niveau / option / classe). Mêmes garde-fous stricts.
    reporterExamenSiDevoirZeroBatch: builder.mutation<{
      dry_run: boolean;
      scope: {
        pole: string; niveau: string; option: string;
        classe: string; semestre: string; annee: string;
      };
      total_matieres: number;
      total_eligibles: number;
      total_appliques: number;
      ignores_globaux: {
        verrouilles: number; deja_note: number;
        pas_examen: number; pas_amelioration: number;
      };
      par_matiere: Array<{
        matiere_id: number;
        code: string;
        nom: string;
        classes: string[];
        nb_eligibles: number;
        nb_appliques: number;
        ignores: { pas_examen: number; pas_amelioration: number };
      }>;
    }, {
      annee_academique: string;
      semestre: string;
      pole?: string;
      niveau?: string;
      option?: string;
      classe?: string;
      dry_run: boolean;
    }>({
      query: (data) => ({
        url: '/notes-devoirs/reporter-examen-si-devoir-zero-batch/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, arg) => arg.dry_run ? [] : ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // Reporte max(SO, SR) au devoir UNIQUEMENT pour ceux à 0 au devoir ET qui
    // ont composé un examen. Garde-fous stricts : verrouillage + moyenne == 0.
    reporterExamenSiDevoirZero: builder.mutation<{
      dry_run: boolean;
      eligibles: Array<{
        note_devoir_id: number;
        etudiant_id: number;
        matricule: string;
        nom: string;
        classe: string;
        ancien_d1: number | null;
        ancien_d2: number | null;
        ancienne_moyenne: number | null;
        so: number | null;
        sr: number | null;
        nouvelle_moyenne: number;
        source: 'SO' | 'SR';
      }>;
      total_eligibles: number;
      applique: number;
      ignores: {
        verrouilles: number; deja_note: number;
        pas_examen: number; pas_amelioration: number;
      };
      matiere_id: number;
      annee_academique: string;
      semestre: string;
    }, {
      matiere_id: number;
      annee_academique: string;
      semestre: string;
      classes?: string[];
      dry_run: boolean;
    }>({
      query: (data) => ({
        url: '/notes-devoirs/reporter-examen-si-devoir-zero/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, arg) => arg.dry_run ? [] : ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    detecterDevoirsNonFaits: builder.query<{
      annee_academique: string; semestre: string;
      total_non_faits: number; total_partiels: number; total_avec_examen: number;
      devoirs_non_faits: Array<{
        matiere_id: number; matiere_nom: string; matiere_code: string;
        classe: string; niveau: string; option: string; pole: string;
        nb_etudiants: number; nb_notes_d1: number; nb_notes_d2: number;
        pct_d1: number; pct_d2: number; a_examen: boolean;
        statut: string; suggestion: string;
      }>;
      devoirs_partiels: Array<any>;
    }, { annee_academique: string; semestre: string; niveau?: string; pole?: string }>({
      query: (params) => ({ url: '/notes-devoirs/detecter_devoirs_non_faits/', params }),
      providesTags: ['NotesDevoirs'],
    }),

    noteCollective: builder.mutation<{
      success: boolean; message: string; crees: number; modifies: number; ignores: number; total_traites: number;
    }, { matiere_id: number; annee_academique: string; semestre: string; classes: string[]; champ: string; valeur: number; ecraser_existants?: boolean }>({
      query: (data) => ({ url: '/notes-devoirs/note_collective/', method: 'POST', body: data }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // ═══ REPORT INTELLIGENT (analyse + report tracé + undo) ═══

    analyserCompletude: builder.mutation<AnalyseCompletudeResponse, AnalyseCompletudeParams>({
      query: (data) => ({ url: '/notes-devoirs/analyser-completude/', method: 'POST', body: data }),
    }),

    reporterDevoirManquant: builder.mutation<ReportBatchResponse, ReporterDevoirManquantParams>({
      query: (data) => ({ url: '/notes-devoirs/reporter-devoir-manquant/', method: 'POST', body: data }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    reporterExamenVersDevoirsTrace: builder.mutation<ReportBatchResponse, ReporterExamenVersDevoirsTraceParams>({
      query: (data) => ({ url: '/notes-devoirs/reporter-examen-vers-devoirs-trace/', method: 'POST', body: data }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    annulerBatch: builder.mutation<{ success: boolean; batch_id: string; nb_modifications_restaurees: number; nb_notes_devoirs_affectees: number },
                                    { batch_id: string; commentaire?: string }>({
      query: (data) => ({ url: '/notes-devoirs/annuler-batch/', method: 'POST', body: data }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    getBatchesRecents: builder.query<{ batches: BatchRecent[] }, { limit?: number; type_operation?: string }>({
      query: (params) => ({ url: '/notes-devoirs/batches-recents/', params }),
    }),

    // ─── Outils avancés (anomalies / arrondi / plafond) ──────────────────
    detecterAnomaliesDevoirs: builder.query<AnomaliesDevoirsResponse, AnomaliesDevoirsParams>({
      query: (params) => ({ url: '/notes-devoirs/detecter-anomalies/', params }),
      providesTags: ['NotesDevoirs'],
    }),

    arrondirNotesDevoirs: builder.mutation<ArrondirPlafondResponse, ArrondirNotesDevoirsParams>({
      query: (body) => ({ url: '/notes-devoirs/arrondir-notes/', method: 'POST', body }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    plafondPlancherDevoirs: builder.mutation<ArrondirPlafondResponse, PlafondPlancherDevoirsParams>({
      query: (body) => ({ url: '/notes-devoirs/plafond-plancher/', method: 'POST', body }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),

    // Fix 2026-09-08 : dispositif anti-réclamations
    autoRemplirZeros: builder.mutation<AutoRemplirZerosResponse, AutoRemplirZerosParams>({
      query: (body) => ({ url: '/notes-devoirs/auto-remplir-zeros/', method: 'POST', body }),
      invalidatesTags: ['NotesDevoirs', 'Stats', 'HistoriqueModifications'],
    }),
    auditAntiReclamations: builder.query<AuditAntiReclamationsResponse, AuditAntiReclamationsParams>({
      query: (params) => ({ url: '/notes-devoirs/audit-anti-reclamations/', params }),
      providesTags: ['NotesDevoirs'],
    }),
    apercuNotesMatiereClasse: builder.query<ApercuNotesResponse, ApercuNotesParams>({
      query: (params) => ({ url: '/notes-devoirs/apercu-notes-matiere-classe/', params }),
      providesTags: ['NotesDevoirs'],
    }),
  }),
});

// ─── Types dispositif anti-réclamations ────────────────────────────────────
export interface AutoRemplirZerosParams {
  matiere_id: number;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  classes?: string[];
  cible?: 'devoirs' | 'examen' | 'both';
  est_rattrapage?: boolean;
  seuil_min_notes?: number;
  dry_run?: boolean;
  apply_all?: boolean;
  motif?: string;
  commentaire?: string;
}
export interface AutoRemplirZerosResponse {
  success: boolean;
  dry_run: boolean;
  batch_id: string | null;
  matiere: { id: number; code: string; nom: string; niveau: string };
  classes_scannees: string[];
  nb_etudiants: number;
  garde_fou: {
    seuil_min_notes: number;
    devoirs_note1_positives: number;
    devoirs_note2_positives: number;
    examens_positifs: number;
    gf_devoirs_ok: boolean;
    gf_examens_ok: boolean;
    apply_all: boolean;
  };
  agir_devoirs: boolean;
  agir_examen: boolean;
  d1_remplis: number;
  d2_remplis: number;
  examens_remplis: number;
  note_devoirs_creees: number;
  note_examens_creees: number;
}
export interface AuditAntiReclamationsParams {
  annee_academique?: string;
  semestre?: 'S1' | 'S2';
  classe?: string;
  niveau?: string;
  pole?: string;
  seuil_moitie?: number;
  est_rattrapage?: boolean;
}
export interface AuditAlerteEntry {
  matiere_id: number;
  matiere_code: string;
  matiere_nom: string;
  niveau: string;
  semestre_absolu: number;
  semestre_relatif: 'S1' | 'S2';
  option: string;
  classe: string;
  nb_etudiants: number;
  severite: 'critique' | 'alerte' | 'attention' | 'info';
  type_alerte: string;
  message: string;
  stats: Record<string, number>;
  // Fix 2026-09-08 : formateur(s) attribué(s)
  enseignants?: Array<{ id: number; nom_complet: string }>;
  enseignant_label?: string;
}
export interface AuditAntiReclamationsResponse {
  annee: string;
  semestres: string[];
  seuil_moitie: number;
  nb_alertes: number;
  nb_matieres_scannees: number;
  alertes: AuditAlerteEntry[];
}

// Fix 2026-09-08 : aperçu des notes d'une matière×classe
export interface ApercuNotesParams {
  matiere_id: number;
  classe: string;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  est_rattrapage?: boolean;
}
export interface ApercuNoteCell {
  valeur: number | null;
  etat: 'vide' | 'zero' | 'positif' | 'absent';
}
export interface ApercuNoteLigne {
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  d1: ApercuNoteCell;
  d2: ApercuNoteCell;
  examen: ApercuNoteCell;
  moyenne_devoirs: number | null;
}
export interface ApercuNotesResponse {
  matiere: { id: number; code: string; nom: string; niveau: string; option: string };
  classe: string;
  annee_academique: string;
  semestre: string;
  est_rattrapage: boolean;
  nb_etudiants: number;
  synthese: {
    d1: { vide: number; zero: number; positif: number };
    d2: { vide: number; zero: number; positif: number };
    examen: { vide: number; zero: number; positif: number; absent: number };
  };
  lignes: ApercuNoteLigne[];
}

// ─── Types outils avancés devoirs ───────────────────────────────────────────
export interface AnomaliesDevoirsParams {
  annee_academique: string;
  semestre: string;
  niveau?: string;
  classe?: string;
  matiere_id?: number;
}
export interface AnomalieDevoirEntry {
  id: number;
  matricule: string | null;
  nom: string | null;
  classe: string | null;
  matiere: string | null;
  matiere_code: string | null;
  note1: number | null;
  note2: number | null;
  absent1: boolean;
  absent2: boolean;
  problemes: string[];
}
export interface AnomaliesDevoirsResponse {
  annee_academique: string;
  semestre: string;
  total_anomalies: number;
  hors_plage: AnomalieDevoirEntry[];
  incoherentes: AnomalieDevoirEntry[];
  incompletes: AnomalieDevoirEntry[];
}
export interface ArrondirNotesDevoirsParams {
  matiere_id: number;
  classes: string[];
  annee_academique: string;
  semestre: string;
  palier: 0.25 | 0.5 | 1;
  sens?: 'proche' | 'sup' | 'inf';
  motif?: string;
  commentaire?: string;
}
export interface PlafondPlancherDevoirsParams {
  matiere_id: number;
  classes: string[];
  annee_academique: string;
  semestre: string;
  plancher?: number | null;
  plafond?: number | null;
  motif?: string;
  commentaire?: string;
}
export interface ArrondirPlafondResponse {
  success: boolean;
  batch_id: string;
  palier?: number;
  sens?: string;
  plancher?: number | null;
  plafond?: number | null;
  nb_modifies?: number;
  nb_inchangees?: number;
  nb_relevees?: number;
  nb_abaissees?: number;
  recalcul?: { ecue: number; ue: number; semestre: number; errors: number } | null;
}

// ─── Types pour le report intelligent ────────────────────────────────────────

export interface AnalyseCompletudeParams {
  matiere_id: number;
  annee_academique: string;
  semestre: string;
  classes: string[];
}

export interface EtudiantManquant {
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  note_d1: number | null;
  note_d2: number | null;
  absent_d1: boolean;
  absent_d2: boolean;
  note_examen: number | null;
  absent_examen: boolean;
}

export interface AnalyseCompletudeResponse {
  matiere_id: number;
  classes: string[];
  annee_academique: string;
  semestre: string;
  total_etudiants: number;
  stats: {
    nb_d1: number; nb_d2: number; nb_examen: number;
    pct_d1: number; pct_d2: number; pct_examen: number;
  };
  manque_d1: EtudiantManquant[];
  manque_d2: EtudiantManquant[];
  manque_les_deux: EtudiantManquant[];
  manque_examen: EtudiantManquant[];
  nb_complets: number;
}

export interface ReporterDevoirManquantParams {
  matiere_id: number;
  annee_academique: string;
  semestre: string;
  etudiants_ids: number[];
  motif?: string;
  commentaire?: string;
}

export interface ReporterExamenVersDevoirsTraceParams {
  matiere_id: number;
  annee_academique: string;
  semestre: string;
  etudiants_ids: number[];
  mode?: 'manquants_seulement' | 'tous';
  motif?: string;
  commentaire?: string;
}

export interface ReportBatchResponse {
  success: boolean;
  batch_id: string;
  type_operation: string;
  nb_etudiants_concernes: number;
  nb_reportes?: number;
  nb_crees?: number;
  nb_modifies?: number;
  nb_ignores: number;
  details: any[];
}

export interface BatchRecent {
  batch_id: string;
  type_operation: string;
  date: string;
  nb_modifications: number;
  auteur: string | null;
  matiere_code: string | null;
  matiere_nom: string | null;
  commentaire: string;
  annule: boolean;
}

export const {
  useGetNotesDevoirsQuery,
  useGetNoteDevoirQuery,
  useSaisieMatricielleMutation,
  useSaisieIndividuelleMutation,
  useSaisieLigneMutation,
  usePreviewExcelMutation,
  useImportExcelMutation,
  useVerrouillerMutation,
  useDeverrouillerMutation,
  useGetRapportImpressionQuery,
  useLazyGetRapportImpressionQuery,
  useGetStatsQuery,
  useGetHistoriqueQuery,
  useGetHistoriqueDetailQuery,
  useGetMatriceQuery,
  useBulkUpdateMutation,
  useReporterExamenVersDevoirsMutation,
  useReporterExamenSiDevoirZeroMutation,
  useReporterExamenSiDevoirZeroBatchMutation,
  useDetecterDevoirsNonFaitsQuery,
  useNoteCollectiveMutation,
  useAnalyserCompletudeMutation,
  useReporterDevoirManquantMutation,
  useReporterExamenVersDevoirsTraceMutation,
  useAnnulerBatchMutation,
  useGetBatchesRecentsQuery,
  useLazyGetBatchesRecentsQuery,
  useDetecterAnomaliesDevoirsQuery,
  useArrondirNotesDevoirsMutation,
  usePlafondPlancherDevoirsMutation,
  useAutoRemplirZerosMutation,
  useAuditAntiReclamationsQuery,
  useLazyAuditAntiReclamationsQuery,
  useApercuNotesMatiereClasseQuery,
  useLazyApercuNotesMatiereClasseQuery,
} = notesDevoirsApi;
